// src/components/website-preview/NewsletterSignup.js
import React, { useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { hexToRgba } from '../../utils/colorUtils';
import { trackEvent } from '../../utils/analytics';

const NewsletterSignup = ({ config, colors: propColors, fonts: propFonts, buttonStyle: propButtonStyle, headingStyle: propHeadingStyle, practitionerUid }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Use props passed from WebsitePreview (which has the correctly computed values)
  const colors = propColors || { primary: '#7c3aed', text: '#1F2937', background: '#FFFFFF', secondaryText: '#6B7280' };
  const fonts = propFonts || { heading: 'Montserrat, sans-serif', body: 'Montserrat, sans-serif' };
  const buttonStyle = propButtonStyle || {};
  const headingStyle = propHeadingStyle || {};
  const layout = config?.layout || 'centered-box';

  // Calculate border radius from buttonStyle
  const borderRadius = buttonStyle?.cornerRadius === 'sharp' ? '0px'
    : buttonStyle?.cornerRadius === 'very-rounded' ? '9999px'
    : '8px';

  // Calculate padding from buttonStyle.size
  const buttonPadding = buttonStyle?.size === 'compact' ? '8px 16px'
    : buttonStyle?.size === 'large' ? '14px 32px'
    : '10px 24px';

  const buttonFontSize = buttonStyle?.size === 'compact' ? '13px'
    : buttonStyle?.size === 'large' ? '16px'
    : '14px';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError(null);

    fetch('https://us-west1-divine-frameworks-6cb16.cloudfunctions.net/subscribeToNewsletter', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ practitionerId: practitionerUid, email: email.trim() })
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setSubmitted(true);
          trackEvent('newsletter_signup', { practitioner_id: practitionerUid });
          setName('');
          setEmail('');
          setTimeout(() => setSubmitted(false), 5000);
        } else {
          setSubmitError('Something went wrong. Please try again.');
        }
        setIsSubmitting(false);
      })
      .catch(() => {
        setSubmitError('Something went wrong. Please try again.');
        setIsSubmitting(false);
      });
  };

  const inputStyle = {
    padding: '12px 16px',
    border: '1px solid #E5E7EB',
    borderRadius,
    fontSize: '14px',
    fontFamily: fonts.body,
    backgroundColor: '#FFFFFF',
    color: '#1F2937',
    transition: 'all 0.2s ease',
  };

  const primaryButtonStyle = {
    padding: buttonPadding,
    fontSize: buttonFontSize,
    fontWeight: '600',
    borderRadius,
    border: 'none',
    backgroundColor: colors.primary,
    color: '#FFFFFF',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: fonts.body,
  };

  // ============================
  // Centered Box Layout
  // ============================
  if (layout === 'centered-box') {
    return (
      <div style={{ padding: '64px 24px', backgroundColor: '#F9FAFB' }}>
        <div style={{ maxWidth: '560px', margin: '0 auto', backgroundColor: 'white', borderRadius: '12px', padding: '40px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          {!submitted ? (
            <>
              <h2 style={{ ...headingStyle, marginBottom: '12px' }}>
                {config.headline}
              </h2>

              {config.description && (
                <p
                  style={{
                    fontFamily: fonts.body,
                    fontSize: '15px',
                    color: colors.secondaryText || '#6B7280',
                    textAlign: 'center',
                    marginBottom: '32px',
                  }}
                >
                  {config.description}
                </p>
              )}

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {config.showNameField && (
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name (optional)"
                    style={inputStyle}
                  />
                )}
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  style={inputStyle}
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{ ...primaryButtonStyle, opacity: isSubmitting ? 0.6 : 1, marginTop: '8px' }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                </button>
                {submitError && (
                  <p style={{ fontFamily: fonts.body, fontSize: '13px', color: '#DC2626', marginTop: '8px', textAlign: 'center' }}>
                    {submitError}
                  </p>
                )}
              </form>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <CheckCircle style={{ width: '64px', height: '64px', margin: '0 auto 16px', color: colors.primary }} />
              <h3 style={{ fontFamily: fonts.heading, fontSize: '20px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
                Thank you for subscribing!
              </h3>
              <p style={{ fontFamily: fonts.body, fontSize: '14px', color: colors.secondaryText || '#6B7280' }}>
                We'll keep you updated with news and insights.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============================
  // Full-Width Banner Layout
  // ============================
  if (layout === 'full-width') {
    // Use a subtle tint of the primary color as background
    const bannerBg = `linear-gradient(135deg, ${hexToRgba(colors.primary, 0.1)} 0%, ${hexToRgba(colors.primary, 0.05)} 100%)`;

    return (
      <div style={{ padding: '48px 24px', background: bannerBg }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {!submitted ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', alignItems: 'center' }}>
              <div style={{ textAlign: 'center', maxWidth: '600px' }}>
                <h2 style={{ ...headingStyle, fontSize: '28px', marginBottom: '8px' }}>
                  {config.headline}
                </h2>
                {config.description && (
                  <p style={{ fontFamily: fonts.body, fontSize: '15px', color: colors.secondaryText || '#6B7280' }}>
                    {config.description}
                  </p>
                )}
              </div>

              <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '12px', width: '100%', maxWidth: '600px', flexWrap: 'wrap', justifyContent: 'center' }}>
                {config.showNameField && (
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    style={{ ...inputStyle, flex: '1 1 200px', minWidth: '200px' }}
                  />
                )}
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  style={{ ...inputStyle, flex: '1 1 250px', minWidth: '250px' }}
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{ ...primaryButtonStyle, opacity: isSubmitting ? 0.6 : 1 }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                </button>
                {submitError && (
                  <p style={{ fontFamily: fonts.body, fontSize: '13px', color: '#DC2626', width: '100%', textAlign: 'center', marginTop: '4px' }}>
                    {submitError}
                  </p>
                )}
              </form>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', padding: '16px' }}>
              <CheckCircle style={{ width: '32px', height: '32px', color: colors.primary }} />
              <p style={{ fontFamily: fonts.body, fontSize: '16px', fontWeight: '600', color: colors.text }}>
                Thank you for subscribing!
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============================
  // Minimal Inline Layout
  // ============================
  if (layout === 'minimal') {
    return (
      <div style={{ padding: '32px 24px', backgroundColor: '#FAFAFA', borderTop: '1px solid #E5E7EB' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          {!submitted ? (
            <>
              <form onSubmit={handleSubmit} style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
                <span style={{ ...headingStyle, fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap', marginBottom: '0' }}>
                  {config.headline}
                </span>
                {config.showNameField && (
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Name"
                    style={{ ...inputStyle, width: '140px', padding: '8px 12px' }}
                  />
                )}
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  style={{ ...inputStyle, width: '220px', padding: '8px 12px' }}
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{ ...primaryButtonStyle, padding: '8px 20px', fontSize: '13px', opacity: isSubmitting ? 0.6 : 1 }}
                >
                  {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                </button>
              </form>
              {submitError && (
                <p style={{ fontFamily: fonts.body, fontSize: '13px', color: '#DC2626', textAlign: 'center', marginTop: '8px' }}>
                  {submitError}
                </p>
              )}
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
              <CheckCircle style={{ width: '24px', height: '24px', color: colors.primary }} />
              <p style={{ fontFamily: fonts.body, fontSize: '14px', fontWeight: '500', color: colors.text }}>
                Thank you for subscribing!
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default NewsletterSignup;
